/**
 * The server console.
 *
 * What pugo.ink/status shows anybody, plus everything you would want at
 * eleven at night when something is wrong: a live picture of the last two
 * minutes, what the server thinks is wrong and why, the switches that let
 * you stop doing the expensive thing, and the guard that can do it for you.
 *
 * Read by moderators. Changed only by admins - a switch is load-bearing and
 * the guard can hold somebody back, so those are not a moderator's to flip.
 *
 * Polled rather than pushed, at two seconds. A socket for this would be a
 * second protocol to keep working for one page that one person looks at.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useSession } from '@/stores/session';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';
import { Button, Empty, useConfirm, useToast } from '@/components/ui';

type Sample = {
  at: number;
  lag: number;
  rss: number;
  heapUsed: number;
  heapTotal: number;
  sockets: number;
  calls: number;
  /** Host CPU as a percentage. Null on the first sample, which has nothing
      to difference against - see host.js. */
  cpu: number | null;
  /** Bytes in use on the whole box, not by this process. */
  memUsed: number;
};
type Cause = { userId: string; name: string | null; role: string | null; count: number } | null;
type Warning = { id: string; level: 'warn' | 'bad'; metric: number; title: string; note: string; cause: Cause };
type Switch = { id: string; name: string; note: string; on: boolean; reason: string; changedAt: number | null; changedBy: string | null };
type Held = { userId: string; name: string; until: number; why: string };
type Action = { at: number; kind: string; userId: string; name: string | null; until: number; why: string; metric: number };

export type Host = {
  cpu: { busy: number | null; cores: number };
  memory: { total: number; available: number; used: number; cached: number };
  load: { one: number; five: number; fifteen: number; cores: number; perCore: number };
  disk: { total: number; free: number; used: number };
  platform: string;
  release: string;
  hostUptime: number;
};

export type Rec = { value: number; at: number; online?: number; sockets?: number };
export type Records = Record<string, Rec | undefined>;

export type Capacity = {
  people: number;
  perPerson: number;
  measured: boolean;
  overhead: number;
  target: number;
  needBytes: number;
  needWithHeadroom: number;
  haveBytes: number;
  fits: boolean;
  holds: number;
  cpuHeadroom: string;
  worstLag: number;
  upgrades: { what: string; from: string; to: string; why: string }[];
};

type Console = {
  now: number;
  uptime: number;
  version: string;
  build: string;
  node: string;
  platform: string;
  tps: number;
  lagMs: number;
  memory: { rss: number; heapUsed: number; heapTotal: number };
  disk: { total: number; free: number; used: number; dataBytes: number };
  counts: { users: number; online: number; parties: number; messages: number; sockets: number; sessions: number };
  switches: Switch[];
  host: Host;
  capacity: Capacity;
  guard: {
    enabled: boolean;
    samples: Sample[];
    warnings: Warning[];
    throttled: Held[];
    actions: Action[];
    limits: { gapSeconds: number; perHour: number; throttleMinutes: number };
    records: Records;
    host: Host;
    /* The thresholds, each with the range the server will accept - see
       watchdog.js. Optional so an older server does not blank the page. */
    tuning?: TuningState;
  };
  notice: NoticeState;
};

export type NoticeLevel = 'info' | 'warn' | 'bad';
export type NoticeState = {
  on: boolean;
  message: string;
  level: NoticeLevel;
  until: number | null;
  by?: string | null;
  at?: number | null;
  /** On, but past its end time: the one state that looks broken from the
      outside and is worth saying out loud on the page that set it. */
  lapsed?: boolean;
};

export type TuningState = Record<string, {
  value: number;
  def: number;
  min: number;
  max: number;
  label: string;
  unit: string;
}>;

const mb = (n: number) => Math.round(n / 1048576) + ' MB';
const gb = (n: number) => (n / 1073741824).toFixed(1) + ' GB';

function duration(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d) return d + 'd ' + h + 'h';
  if (h) return h + 'h ' + m + 'm';
  return m + 'm';
}

function leftFor(until: number, now: number): string {
  const s = Math.max(0, Math.round((until - now) / 1000));
  return s >= 60 ? Math.ceil(s / 60) + ' min' : s + 's';
}

type Tab = 'live' | 'machine' | 'capacity' | 'controls' | 'debug';

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: 'live', label: 'Live', icon: 'bolt' },
  { id: 'machine', label: 'The machine', icon: 'layers' },
  { id: 'capacity', label: 'Capacity', icon: 'chart' },
  { id: 'controls', label: 'Controls', icon: 'sliders' },
  { id: 'debug', label: 'Debug', icon: 'bug' }
];

export function ServerConsole({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [pausing, setPausing] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  /* How many people the planner is sizing for. Part of the key, so changing
     it asks the server rather than recomputing a stale answer here. */
  const [want, setWant] = useState(500);

  /*
   * Five sections rather than one column eight sections long.
   *
   * Everything here was stacked: warnings, numbers, graphs, the machine, the
   * planner, the records, the switches, the guard, the debug dump. Checking
   * "is it all right" meant scrolling past the capacity planner, and finding
   * a switch meant scrolling past everything. These are five different
   * questions asked at five different moments.
   */
  const [tab, setTab] = useState<Tab>('live');

  /*
   * How often to ask, and it can be turned off.
   *
   * Two seconds is right while you are watching something go wrong and
   * wasteful while you are reading the capacity planner - and this page is
   * open on somebody's second monitor for hours. Paused, it asks nothing at
   * all, which is also the only way to read a graph that will not sit still.
   */
  const [every, setEvery] = useState(2000);

  const q = useQuery({
    queryKey: ['admin', 'console', want],
    queryFn: () => api.get<Console>('/api/admin/console?plan=' + want),
    refetchInterval: every || false,
    /* Keeps the previous answer on screen while the next one is asked, so
       typing a number does not blank the page between keystrokes. */
    placeholderData: (prev) => prev
  });

  const flip = useMutation({
    mutationFn: (v: { id: string; on: boolean; reason?: string }) =>
      api.post('/api/admin/switches/' + v.id, { on: v.on, reason: v.reason }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['admin', 'console'] }); },
    onError: (e) => toast((e as Error).message, 'bad')
  });

  const guard = useMutation({
    mutationFn: (enabled: boolean) => api.post('/api/admin/guard', { enabled }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['admin', 'console'] }); },
    onError: (e) => toast((e as Error).message, 'bad')
  });

  const free = useMutation({
    mutationFn: (userId: string) => api.del('/api/admin/guard/throttle/' + userId),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['admin', 'console'] }); },
    onError: (e) => toast((e as Error).message, 'bad')
  });

  const d = q.data;

  if (q.isPending) return <div className="h-[420px] animate-pulse rounded-[var(--radius-l)] bg-surface-2" />;
  if (q.isError || !d) return <Empty title="Cannot read the server" note={(q.error as Error)?.message} />;

  const lagTone = d.lagMs >= 400 ? 'bad' : d.lagMs >= 120 ? 'warn' : 'good';
  const diskPct = d.disk.total ? Math.round((d.disk.used / d.disk.total) * 100) : 0;
  const heapPct = d.memory.heapTotal ? Math.round((d.memory.heapUsed / d.memory.heapTotal) * 100) : 0;
  const off = d.switches.filter((s) => !s.on);

  return (
    <div className="flex flex-col gap-4">
      {dialog}

      {/*
        The sections, and how often the page asks.

        The warning count rides on the tab it belongs to, so a problem that
        appears while you are reading the planner still says so.
      */}
      <div className="flex flex-wrap items-center gap-2">
        <nav className="no-scrollbar -mx-1 flex min-w-0 flex-1 gap-1 overflow-x-auto px-1">
          {TABS.map((t) => {
            const on = tab === t.id;
            const flag = t.id === 'live' ? d.guard.warnings.length : 0;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                aria-current={on ? 'page' : undefined}
                className={cn(
                  'flex flex-none items-center gap-2 rounded-[calc(11px*var(--radius-mul,1))] px-3 py-2 text-[13.5px] font-semibold whitespace-nowrap outline-none',
                  'transition-[background-color,color,box-shadow] duration-[var(--t-hover)] ease-[var(--ease)]',
                  'focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                  on ? 'bg-accent-soft text-accent' : 'text-mute hover:bg-surface-2 hover:text-ink'
                )}
              >
                <Icon name={t.icon} size={15} className={on ? undefined : 'opacity-60'} />
                {t.label}
                {flag ? (
                  <span className="rounded-full bg-[color-mix(in_srgb,var(--bad)_22%,transparent)] px-1.5 text-[11px] font-bold text-bad">
                    {flag}
                  </span>
                ) : null}
              </button>
            );
          })}
        </nav>

        <label className="flex flex-none items-center gap-2 text-[12.5px] text-mute">
          <span className={cn('h-1.5 w-1.5 rounded-full', every ? 'animate-pulse bg-good' : 'bg-ghost')} />
          <select
            value={every}
            onChange={(e) => setEvery(Number(e.target.value))}
            aria-label="How often to refresh"
            className="rounded-[calc(9px*var(--radius-mul,1))] bg-surface-2 px-2 py-1.5 text-[12.5px] text-dim outline-none shadow-[inset_0_0_0_1px_var(--line-soft)] focus-visible:shadow-[0_0_0_2px_var(--accent)]"
          >
            <option value={1000}>Every second</option>
            <option value={2000}>Every 2s</option>
            <option value={5000}>Every 5s</option>
            <option value={30000}>Every 30s</option>
            <option value={0}>Paused</option>
          </select>
        </label>
      </div>

      {tab === 'live' ? <>
      {/* ---- anything wrong, first ---- */}
      {d.guard.warnings.length ? (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {d.guard.warnings.map((w) => (
            <li
              key={w.id}
              className={cn(
                'flex flex-wrap items-center gap-3 rounded-[calc(14px*var(--radius-mul,1))] px-4 py-3',
                w.level === 'bad'
                  ? 'bg-[color-mix(in_srgb,var(--bad)_14%,transparent)] shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--bad)_40%,transparent)]'
                  : 'bg-[color-mix(in_srgb,var(--warn)_13%,transparent)] shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--warn)_38%,transparent)]'
              )}
            >
              <Icon name="alert" size={17} className={w.level === 'bad' ? 'flex-none text-bad' : 'flex-none text-warn'} />
              <span className="min-w-0 flex-1">
                <span className="block text-[14.5px] font-bold text-ink">{w.title}</span>
                <span className="block text-[13px] text-dim">{w.note}</span>
              </span>
              {w.cause?.userId ? (
                <span className="flex-none rounded-full bg-surface-2 px-2.5 py-1 text-[12px] font-semibold text-dim">
                  {w.cause.name ?? w.cause.userId} · {w.cause.count}/min
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="m-0 flex items-center gap-2 rounded-[calc(14px*var(--radius-mul,1))] bg-[color-mix(in_srgb,var(--good)_12%,transparent)] px-4 py-3 text-[14px] font-semibold text-good">
          <Icon name="checkCirc" size={17} />
          Everything is behaving.
        </p>
      )}

      {/* ---- the numbers ---- */}
      <div className="grid gap-2 [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]">
        <Stat label="Loop lag" value={d.lagMs.toFixed(1)} unit="ms" note={d.tps.toFixed(1) + ' ticks/s'} tone={lagTone} />
        <Stat label="Memory" value={mb(d.memory.rss)} note={heapPct + '% of heap'} tone={heapPct > 92 ? 'bad' : heapPct > 86 ? 'warn' : undefined} />
        <Stat label="Connections" value={String(d.counts.sockets)} note={d.counts.online + ' online'} />
        <Stat label="Disk" value={d.disk.total ? gb(d.disk.free) + ' free' : mb(d.disk.dataBytes)} note={d.disk.total ? diskPct + '% used' : 'Pugo data'} tone={diskPct > 90 ? 'bad' : diskPct > 75 ? 'warn' : undefined} />
        <Stat label="Uptime" value={duration(d.uptime)} note={'v' + d.version + ' · ' + d.build} />
      </div>

      {/* ---- the last two minutes, in four lines you can point at ---- */}
      <section className="rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--line-soft)]">
        <h3 className="m-0 mb-1 text-[15px] font-bold text-ink">The last two minutes</h3>
        <p className="m-0 mb-3 text-[12.5px] text-mute">
          One sample a second. Point at a line to read what it was at that moment; the dashed line is the worst ever seen.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Graph samples={d.guard.samples} field="lag" label="Loop lag" unit="ms" tone="var(--warn)" worst={d.guard.records.worstLag?.value ?? null} />
          <Graph samples={d.guard.samples} field="cpu" label="CPU" unit="%" tone="var(--bad)" worst={d.guard.records.peakCpu?.value ?? null} />
          <Graph samples={d.guard.samples} field="sockets" label="Connections" tone="var(--accent)" worst={d.guard.records.peakSockets?.value ?? null} />
          <Graph samples={d.guard.samples} field="memUsed" label="Memory on the box" tone="var(--good)" format={gb} worst={d.guard.records.peakHostMem?.value ?? null} />
        </div>
      </section>

      </> : null}

      {tab === 'machine' ? <>
        <HostPanel host={d.host} />
        <Records records={d.guard.records} />
      </> : null}

      {tab === 'capacity' ? <Planner capacity={d.capacity} want={want} onWant={setWant} /> : null}

      {tab === 'controls' ? <>
      <NoticeEditor notice={d.notice} isAdmin={isAdmin} />

      {/* ---- what can be paused ---- */}
      <section className="rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--line-soft)]">
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <h3 className="m-0 flex-1 text-[15px] font-bold text-ink">What Pugo is doing</h3>
          {off.length ? (
            <span className="rounded-full bg-[color-mix(in_srgb,var(--warn)_16%,transparent)] px-2.5 py-1 text-[12px] font-bold text-warn">
              {off.length} paused
            </span>
          ) : null}
          {isAdmin && off.length ? (
            <Button size="sm" kind="outline" onClick={() => { void api.post('/api/admin/switches', {}).then(() => qc.invalidateQueries({ queryKey: ['admin', 'console'] })); }}>
              Turn everything back on
            </Button>
          ) : null}
        </div>

        <p className="m-0 mb-3 text-[12.5px] text-mute">
          Signing in, reading and the health check cannot be paused - whatever else is wrong, people must be able to get in and the deploy has to be able to ask if it is up.
        </p>

        <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
          {d.switches.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-3 rounded-[calc(12px*var(--radius-mul,1))] px-2 py-2 transition-[background-color] duration-[var(--t-hover)] hover:bg-surface-2">
              <span className={cn('h-2 w-2 flex-none rounded-full', s.on ? 'bg-good' : 'bg-warn')} />
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-semibold text-ink">{s.name}</span>
                <span className="block text-[12.5px] text-mute">
                  {s.on ? s.note : (s.reason ? 'Paused: ' + s.reason : 'Paused') + (s.changedBy ? ' · by ' + s.changedBy : '')}
                </span>
              </span>
              {isAdmin ? (
                s.on ? (
                  <Button size="sm" kind="outline" disabled={flip.isPending} onClick={() => { setPausing(s.id); setReason(''); }}>
                    Pause
                  </Button>
                ) : (
                  <Button size="sm" kind="primary" disabled={flip.isPending} onClick={() => flip.mutate({ id: s.id, on: true })}>
                    Resume
                  </Button>
                )
              ) : (
                <span className="text-[12px] text-ghost">{s.on ? 'on' : 'paused'}</span>
              )}
            </li>
          ))}
        </ul>

        {pausing ? (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 p-3">
            <span className="text-[13px] font-semibold text-ink">Why are you pausing {d.switches.find((s) => s.id === pausing)?.name}?</span>
            <input
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Disk filling up"
              maxLength={140}
              className="min-w-[200px] flex-1 rounded-[calc(10px*var(--radius-mul,1))] bg-surface-1 px-3 py-2 text-[13.5px] outline-none shadow-[inset_0_0_0_1px_var(--line)] focus:shadow-[inset_0_0_0_1px_var(--accent)]"
            />
            <Button size="sm" kind="ghost" onClick={() => setPausing(null)}>Cancel</Button>
            <Button
              size="sm"
              kind="danger"
              onClick={() => { flip.mutate({ id: pausing, on: false, reason }); setPausing(null); }}
            >
              Pause it
            </Button>
          </div>
        ) : null}
      </section>

      {/* ---- the guard ---- */}
      <section className="rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--line-soft)]">
        <div className="mb-2 flex flex-wrap items-center gap-3">
          <h3 className="m-0 flex-1 text-[15px] font-bold text-ink">Do something about it automatically</h3>
          {isAdmin ? (
            <Button
              size="sm"
              kind={d.guard.enabled ? 'danger' : 'outline'}
              disabled={guard.isPending}
              onClick={async () => {
                if (!d.guard.enabled) {
                  const sure = await confirm(
                    'Let the guard act on its own?',
                    'It will slow down an account that is making hundreds of requests a minute, for ' +
                    d.guard.limits.throttleMinutes + ' minutes at a time. It never bans, never deletes, never touches staff, ' +
                    'and does at most one thing every ' + d.guard.limits.gapSeconds + ' seconds.',
                    { confirmLabel: 'Turn it on' }
                  );
                  if (!sure) return;
                }
                guard.mutate(!d.guard.enabled);
              }}
            >
              {d.guard.enabled ? 'Turn the guard off' : 'Turn the guard on'}
            </Button>
          ) : null}
        </div>

        <p className="m-0 mb-3 text-[12.5px] text-mute">
          {d.guard.enabled
            ? 'On. It can only slow an account down, for ' + d.guard.limits.throttleMinutes + ' minutes, at most ' + d.guard.limits.perHour + ' times an hour. Everything it does is written to the audit log and expires by itself.'
            : 'Off. Warnings above are still watched for and reported; nothing acts on them.'}
        </p>

        {d.guard.throttled.length ? (
          <ul className="m-0 mb-3 flex list-none flex-col gap-1.5 p-0">
            {d.guard.throttled.map((h) => (
              <li key={h.userId} className="flex flex-wrap items-center gap-3 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 px-3 py-2">
                <Icon name="clock" size={15} className="flex-none text-warn" />
                <span className="min-w-0 flex-1 text-[13.5px] text-ink">
                  <span className="font-semibold">{h.name}</span>
                  <span className="text-mute"> slowed for another {leftFor(h.until, d.now)}</span>
                </span>
                <Button size="sm" kind="ghost" disabled={free.isPending} onClick={() => free.mutate(h.userId)}>Let them go</Button>
              </li>
            ))}
          </ul>
        ) : null}

        <h4 className="m-0 mb-1.5 text-[12px] font-bold tracking-[.08em] text-mute uppercase">What it has done</h4>
        {d.guard.actions.length ? (
          <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {d.guard.actions.map((a, i) => (
              <li key={i} className="flex flex-wrap items-baseline gap-2 text-[12.5px] text-dim">
                <span className="text-ghost">{new Date(a.at).toLocaleTimeString()}</span>
                <span>slowed <span className="font-semibold text-ink">{a.name ?? a.userId}</span></span>
                <span className="text-mute">· {a.why} ({a.metric})</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="m-0 text-[12.5px] text-ghost">Nothing yet.</p>
        )}
      </section>

      <Tuning tuning={d.guard.tuning} isAdmin={isAdmin} />
      </> : null}

      {tab === 'debug' ? <Debug d={d} /> : null}
    </div>
  );
}

/* ------------------------------------------------------------ pieces */

function Stat({ label, value, unit, note, tone }: {
  label: string; value: string; unit?: string; note?: string; tone?: 'good' | 'warn' | 'bad';
}) {
  return (
    <div className="rounded-[calc(14px*var(--radius-mul,1))] bg-surface-1 px-3.5 py-3 shadow-[inset_0_0_0_1px_var(--line-soft)]">
      <p className="m-0 text-[11.5px] font-bold tracking-[.06em] text-mute uppercase">{label}</p>
      <p className={cn(
        'm-0 mt-0.5 text-[22px] font-extrabold tracking-[-.02em]',
        tone === 'bad' ? 'text-bad' : tone === 'warn' ? 'text-warn' : tone === 'good' ? 'text-good' : 'text-ink'
      )}>
        {value}{unit ? <span className="ml-0.5 text-[13px] font-bold text-mute">{unit}</span> : null}
      </p>
      {note ? <p className="m-0 text-[12px] text-mute">{note}</p> : null}
    </div>
  );
}

/* ------------------------------------------------------------ the graph */

/**
 * A line you can point at.
 *
 * The sparkline said "something happened around there" and nothing else.
 * Reading a graph means asking what a spike actually was, so this one has a
 * crosshair: the pointer picks the nearest sample and the readout says the
 * value and how long ago, which is the whole of what anybody wants from it.
 *
 * Still one path and a couple of spans rather than a charting library - 120
 * points redrawn every two seconds does not need three hundred kilobytes of
 * dependency, and a library's default tooltip would be wrong in both themes.
 */
function Graph({ samples, field, label, unit, tone, format, worst }: {
  samples: Sample[];
  field: 'lag' | 'sockets' | 'cpu' | 'memUsed';
  label: string;
  unit?: string;
  tone: string;
  /** How to write one value out. Bytes are not milliseconds. */
  format?: (v: number) => string;
  /** The all-time worst, drawn as a line across so now has something to sit against. */
  worst?: number | null;
}) {
  const [at, setAt] = useState<number | null>(null);

  const values = useMemo(
    () => samples.map((s) => (s[field] === null || s[field] === undefined ? 0 : Number(s[field]))),
    [samples, field]
  );

  if (values.length < 2) {
    return (
      <div className="rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 px-3 py-6 text-center text-[12px] text-ghost">
        {label} — waiting for a second sample
      </div>
    );
  }

  const top = Math.max(1, ...values, worst ?? 0);
  const step = 100 / (values.length - 1);
  const y = (v: number) => 34 - (v / top) * 31;
  const path = values.map((v, i) => (i ? 'L' : 'M') + (i * step).toFixed(2) + ' ' + y(v).toFixed(2)).join(' ');
  /* The same line closed to the floor, so the area under it can be filled -
     a line alone reads as thin at this height. */
  const area = path + ' L100 36 L0 36 Z';

  const show = at !== null ? Math.max(0, Math.min(values.length - 1, at)) : null;
  const shown = show === null ? null : values[show]!;
  const write = format ?? ((v: number) => String(Math.round(v)) + (unit ?? ''));
  const agoMs = show === null ? 0 : (samples[samples.length - 1]!.at - samples[show]!.at);

  return (
    <div className="min-w-0">
      <div className="mb-1 flex items-baseline gap-2">
        <p className="m-0 text-[11.5px] font-bold tracking-[.05em] text-mute uppercase">{label}</p>
        <p className="m-0 min-w-0 flex-1 truncate text-[12px] tabular-nums text-dim">
          {shown !== null
            ? write(shown) + (agoMs > 1500 ? ' · ' + Math.round(agoMs / 1000) + 's ago' : ' · now')
            : write(values[values.length - 1]!) + ' · now'}
        </p>
        {worst ? <p className="m-0 flex-none text-[11px] tabular-nums text-ghost">worst {write(worst)}</p> : null}
      </div>

      <div
        className="relative"
        onPointerLeave={() => setAt(null)}
        onPointerMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setAt(Math.round(((e.clientX - r.left) / Math.max(1, r.width)) * (values.length - 1)));
        }}
      >
        <svg viewBox="0 0 100 36" preserveAspectRatio="none" className="block h-[64px] w-full" aria-hidden>
          <path d={area} fill={tone} opacity="0.13" />
          <path d={path} fill="none" stroke={tone} strokeWidth="1" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
          {worst ? (
            <line x1="0" x2="100" y1={y(worst)} y2={y(worst)} stroke={tone} strokeWidth="1" strokeDasharray="3 3" opacity="0.4" vectorEffect="non-scaling-stroke" />
          ) : null}
          {show !== null ? (
            <>
              <line x1={show * step} x2={show * step} y1="0" y2="36" stroke="currentColor" strokeWidth="1" opacity="0.35" vectorEffect="non-scaling-stroke" className="text-ink" />
              <circle cx={show * step} cy={y(shown!)} r="2" fill={tone} vectorEffect="non-scaling-stroke" />
            </>
          ) : null}
        </svg>
      </div>
    </div>
  );
}
/* --------------------------------------------------------- the machine */

/**
 * The box, not the process.
 *
 * process.memoryUsage() says what Pugo is holding, which is a different
 * question from whether there is room left - and a tidy 200MB process on a
 * host that is out of memory is still about to fall over.
 */
function HostPanel({ host }: { host: Host }) {
  const memPct = host.memory.total ? Math.round((host.memory.used / host.memory.total) * 100) : 0;
  const diskPct = host.disk.total ? Math.round((host.disk.used / host.disk.total) * 100) : 0;
  const cpuPct = host.cpu.busy === null ? null : Math.round(host.cpu.busy * 100);
  const loadPct = Math.round(host.load.perCore * 100);

  return (
    <section className="rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--line-soft)]">
      <h3 className="m-0 mb-1 text-[15px] font-bold text-ink">The machine</h3>
      <p className="m-0 mb-3 text-[12.5px] text-mute">
        {host.platform} · {host.load.cores} {host.load.cores === 1 ? 'core' : 'cores'} · up {duration(host.hostUptime)}
      </p>

      <div className="flex flex-col gap-2.5">
        <Meter
          label="CPU"
          value={cpuPct === null ? 0 : cpuPct}
          text={cpuPct === null ? 'reading…' : cpuPct + '%'}
          note={'load ' + host.load.one.toFixed(2) + ' over ' + host.load.cores + ' · ' + loadPct + '% per core'}
          tone={loadPct > 90 ? 'bad' : loadPct > 70 ? 'warn' : 'good'}
        />
        <Meter
          label="Memory"
          value={memPct}
          text={gb(host.memory.available) + ' free'}
          note={gb(host.memory.used) + ' of ' + gb(host.memory.total) + ' in use' + (host.memory.cached ? ' · ' + gb(host.memory.cached) + ' is cache the kernel would hand back' : '')}
          tone={memPct > 92 ? 'bad' : memPct > 80 ? 'warn' : 'good'}
        />
        <Meter
          label="Disk"
          value={diskPct}
          text={gb(host.disk.free) + ' free'}
          note={gb(host.disk.used) + ' of ' + gb(host.disk.total) + ' used'}
          tone={diskPct > 90 ? 'bad' : diskPct > 75 ? 'warn' : 'good'}
        />
      </div>
    </section>
  );
}

/** One labelled bar. The number and what it means, on one line each. */
function Meter({ label, value, text, note, tone }: {
  label: string; value: number; text: string; note: string; tone: 'good' | 'warn' | 'bad';
}) {
  const colour = tone === 'bad' ? 'var(--bad)' : tone === 'warn' ? 'var(--warn)' : 'var(--good)';
  return (
    <div className="min-w-0">
      <div className="mb-1 flex items-baseline gap-2">
        <p className="m-0 w-[58px] flex-none text-[11.5px] font-bold tracking-[.05em] text-mute uppercase">{label}</p>
        <p className="m-0 flex-none text-[13px] font-bold tabular-nums text-ink">{text}</p>
        <p className="m-0 min-w-0 flex-1 truncate text-right text-[11.5px] text-ghost">{note}</p>
      </div>
      <div className="h-[6px] w-full overflow-hidden rounded-full bg-surface-3">
        <span
          className="block h-full rounded-full transition-[width] duration-500 ease-[var(--ease-out)]"
          style={{ width: Math.min(100, Math.max(0, value)) + '%', background: colour }}
        />
      </div>
    </div>
  );
}

/* ----------------------------------------------------------- the worst */

/**
 * What it has done at its worst, kept across restarts.
 *
 * A two-minute graph cannot answer "did it struggle last night", because by
 * the time anybody looks the two minutes have moved on. Each of these says
 * how many people were on at the time, which is the part that makes it mean
 * something - 400ms of lag at three people is a bug, at three hundred it is
 * capacity.
 */
function Records({ records }: { records: Records }) {
  const rows: { label: string; rec?: Rec; write: (v: number) => string }[] = [
    { label: 'Worst loop lag', rec: records.worstLag, write: (v) => Math.round(v) + 'ms' },
    { label: 'Busiest CPU', rec: records.peakCpu, write: (v) => Math.round(v) + '%' },
    { label: 'Most memory Pugo held', rec: records.peakRss, write: mb },
    { label: 'Most memory on the box', rec: records.peakHostMem, write: gb },
    { label: 'Most connections', rec: records.peakSockets, write: (v) => String(Math.round(v)) },
    { label: 'Most people online', rec: records.peakOnline, write: (v) => String(Math.round(v)) }
  ];
  const any = rows.some((r) => r.rec);

  return (
    <section className="rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--line-soft)]">
      <h3 className="m-0 mb-1 text-[15px] font-bold text-ink">The worst it has been</h3>
      <p className="m-0 mb-3 text-[12.5px] text-mute">
        Kept across restarts. The number of people at the time is what makes these mean something.
      </p>
      {any ? (
        <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
          {rows.filter((r) => r.rec).map((r) => (
            <li key={r.label} className="flex flex-wrap items-baseline gap-x-2 text-[13px]">
              <span className="min-w-0 flex-1 truncate text-dim">{r.label}</span>
              <span className="flex-none font-bold tabular-nums text-ink">{r.write(r.rec!.value)}</span>
              <span className="w-full text-[11.5px] text-ghost sm:w-auto sm:flex-none">
                {new Date(r.rec!.at).toLocaleString()}
                {r.rec!.online !== undefined ? ' · ' + r.rec!.online + ' online' : ''}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="m-0 text-[12.5px] text-ghost">Nothing recorded yet.</p>
      )}
    </section>
  );
}

/* -------------------------------------------------------- the planner */

/**
 * What it would take to hold more people.
 *
 * Measured rather than guessed: cost per head is the working set above the
 * smallest RSS the process has been seen at, divided by the people who were
 * on. It says plainly when that measurement is too thin to trust and falls
 * back to a stated assumption rather than quietly inventing a number.
 */
function Planner({ capacity, want, onWant }: {
  capacity: Capacity;
  want: number;
  onWant: (n: number) => void;
}) {
  return (
    <section className="rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--line-soft)]">
      <h3 className="m-0 mb-1 text-[15px] font-bold text-ink">What would it take</h3>
      <p className="m-0 mb-3 text-[12.5px] text-mute">
        Put a number of people in and this says whether the box holds them, and what to buy if it does not.
      </p>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2 text-[13px] text-dim">
          Plan for
          <input
            type="number"
            min={1}
            max={1000000}
            value={want}
            onChange={(e) => onWant(Math.max(1, Math.min(1000000, Number(e.target.value) || 1)))}
            className="w-[110px] rounded-[calc(10px*var(--radius-mul,1))] bg-surface-2 px-2.5 py-1.5 text-[13px] tabular-nums outline-none shadow-[inset_0_0_0_1px_var(--line)] focus:shadow-[inset_0_0_0_1px_var(--accent)]"
          />
          people
        </label>
        {[100, 500, 2000, 10000].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onWant(n)}
            className={cn(
              'rounded-full px-2.5 py-1 text-[12px] font-semibold outline-none transition-colors',
              want === n ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-mute hover:text-ink'
            )}
          >
            {n.toLocaleString()}
          </button>
        ))}
      </div>

      <p className={cn(
        'm-0 mb-3 rounded-[calc(12px*var(--radius-mul,1))] px-3 py-2.5 text-[13.5px] font-semibold',
        capacity.fits
          ? 'bg-[color-mix(in_srgb,var(--good)_13%,transparent)] text-good'
          : 'bg-[color-mix(in_srgb,var(--warn)_14%,transparent)] text-warn'
      )}>
        {capacity.fits
          ? 'This box holds ' + capacity.target.toLocaleString() + '. On these numbers it would take about ' + capacity.holds.toLocaleString() + ' before memory is the limit.'
          : 'This box does not hold ' + capacity.target.toLocaleString() + '. It takes about ' + capacity.holds.toLocaleString() + ' as it is.'}
      </p>

      <dl className="m-0 mb-3 grid gap-x-4 gap-y-1.5 text-[12.5px] [grid-template-columns:auto_1fr]">
        <dt className="text-mute">Per person</dt>
        <dd className="m-0 tabular-nums text-ink">
          {mb(capacity.perPerson)}
          <span className="ml-1.5 text-ghost">
            {capacity.measured
              ? 'measured from ' + capacity.people + ' online'
              : 'assumed — under ' + 5 + ' people online there is nothing to measure'}
          </span>
        </dd>
        <dt className="text-mute">Pugo idle</dt>
        <dd className="m-0 tabular-nums text-ink">{mb(capacity.overhead)}<span className="ml-1.5 text-ghost">smallest seen</span></dd>
        <dt className="text-mute">Needs</dt>
        <dd className="m-0 tabular-nums text-ink">{gb(capacity.needWithHeadroom)}<span className="ml-1.5 text-ghost">including a quarter over, for the kernel and cache</span></dd>
        <dt className="text-mute">Has</dt>
        <dd className="m-0 tabular-nums text-ink">{gb(capacity.haveBytes)}</dd>
        <dt className="text-mute">Loop headroom</dt>
        <dd className="m-0 text-ink">
          {capacity.cpuHeadroom}
          <span className="ml-1.5 text-ghost">worst tick {Math.round(capacity.worstLag)}ms</span>
        </dd>
      </dl>

      {capacity.upgrades.length ? (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {capacity.upgrades.map((u) => (
            <li key={u.what} className="rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 px-3 py-2.5">
              <p className="m-0 text-[13px] font-bold text-ink">
                {u.what}: {u.from} → <span className="text-accent">{u.to}</span>
              </p>
              <p className="m-0 mt-0.5 text-[12px] leading-[1.45] text-mute">{u.why}</p>
            </li>
          ))}
        </ul>
      ) : (
        <p className="m-0 text-[12.5px] text-good">Nothing to upgrade for that number.</p>
      )}
    </section>
  );
}

/* ---------------------------------------------------------- the debug */

/** Everything else, for when the question is "what exactly is running". */
/**
 * The line everybody sees, and who gets to write it.
 *
 * The switches below can pause uploads or voice, and until now nothing could
 * say why. From the outside a maintenance window looked like the thing you
 * were doing quietly breaking, so people reinstall, or assume Pugo is dead
 * and go somewhere else. One sentence prevents all of that.
 *
 * It is deliberately small: a line of text, how loud, and when it stops. An
 * end time is offered first because a notice left up for three weeks is
 * worse than no notice, and somebody always forgets.
 */
function NoticeEditor({ notice, isAdmin }: { notice: NoticeState; isAdmin: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [message, setMessage] = useState(notice.message);
  const [level, setLevel] = useState<NoticeLevel>(notice.level);
  const [minutes, setMinutes] = useState(30);
  /* Stops the server's copy overwriting what is being typed. Once you touch
     the field it is yours until you save or clear it. */
  const [mine, setMine] = useState(false);

  useEffect(() => {
    if (mine) return;
    setMessage(notice.message);
    setLevel(notice.level);
  }, [notice.message, notice.level, mine]);

  const save = useMutation({
    mutationFn: (body: { on: boolean; message?: string; level?: string; minutes?: number }) =>
      api.post('/api/admin/notice', body),
    onSuccess: (_r, body) => {
      setMine(false);
      toast(body.on ? 'Everybody can see it now' : 'Notice taken down', 'good');
      void qc.invalidateQueries({ queryKey: ['admin', 'console'] });
      void qc.invalidateQueries({ queryKey: ['server', 'notice'] });
    },
    onError: (e) => toast((e as Error).message, 'bad')
  });

  const tone = level === 'bad' ? 'var(--bad)' : level === 'warn' ? 'var(--warn)' : 'var(--accent)';
  const text = message.trim();

  return (
    <section className="rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--line-soft)]">
      <div className="mb-1 flex flex-wrap items-center gap-3">
        <h3 className="m-0 flex-1 text-[15px] font-bold text-ink">Tell everybody something</h3>
        {notice.on && !notice.lapsed ? (
          <span className="rounded-full bg-[color-mix(in_srgb,var(--good)_18%,transparent)] px-2.5 py-1 text-[12px] font-bold text-good">
            Showing now
          </span>
        ) : null}
        {notice.lapsed ? (
          <span className="rounded-full bg-surface-2 px-2.5 py-1 text-[12px] font-bold text-mute">Ran out</span>
        ) : null}
      </div>

      <p className="m-0 mb-3 text-[12.5px] text-mute">
        One line across the top of the app, for everybody, signed in or not. Pausing something below tells nobody why it stopped working.
      </p>

      {/* What it will look like, at the size it will be. A banner is the one
          thing worth seeing before it goes out to everybody. */}
      <div
        className="mb-3 flex items-center gap-2.5 rounded-[calc(12px*var(--radius-mul,1))] px-3 py-2 text-[13px]"
        style={{
          background: 'color-mix(in srgb, ' + tone + ' 15%, var(--bg-1))',
          boxShadow: 'inset 0 0 0 1px color-mix(in srgb, ' + tone + ' 35%, transparent)'
        }}
      >
        <Icon name={level === 'info' ? 'info' : 'alert'} size={15} className="flex-none" style={{ color: tone }} />
        <span className={cn('min-w-0 flex-1 truncate font-semibold', text ? 'text-ink' : 'text-ghost')}>
          {text || 'Nothing is being shown.'}
        </span>
      </div>

      <div className="flex flex-col gap-2.5">
        <input
          value={message}
          onChange={(e) => { setMine(true); setMessage(e.target.value); }}
          placeholder="Voice is off while we move the server. Back in about 20 minutes."
          maxLength={200}
          disabled={!isAdmin}
          className="w-full rounded-[calc(11px*var(--radius-mul,1))] bg-surface-2 px-3 py-2.5 text-[13.5px] text-ink outline-none shadow-[inset_0_0_0_1px_var(--line)] placeholder:text-ghost focus:shadow-[inset_0_0_0_1px_var(--accent)] disabled:opacity-60"
        />

        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex gap-1 rounded-[calc(11px*var(--radius-mul,1))] bg-surface-2 p-1">
            {([
              ['info', 'Heads up'],
              ['warn', 'Something is off'],
              ['bad', 'It is broken']
            ] as [NoticeLevel, string][]).map(([id, label]) => (
              <button
                key={id}
                type="button"
                disabled={!isAdmin}
                onClick={() => { setMine(true); setLevel(id); }}
                className={cn(
                  'rounded-[calc(8px*var(--radius-mul,1))] px-2.5 py-1.5 text-[12.5px] font-semibold outline-none',
                  'transition-[background-color,color] duration-[var(--t-hover)] ease-[var(--ease)]',
                  'focus-visible:shadow-[0_0_0_2px_var(--accent)] disabled:opacity-60',
                  level === id ? 'bg-surface-0 text-ink shadow-[0_1px_2px_rgba(0,0,0,.25)]' : 'text-mute hover:text-dim'
                )}
              >
                {label}
              </button>
            ))}
          </div>

          <label className="flex items-center gap-1.5 text-[12.5px] text-mute">
            for
            <select
              value={minutes}
              disabled={!isAdmin}
              onChange={(e) => setMinutes(Number(e.target.value))}
              className="rounded-[calc(9px*var(--radius-mul,1))] bg-surface-2 px-2 py-1.5 text-[12.5px] text-dim outline-none shadow-[inset_0_0_0_1px_var(--line-soft)] focus-visible:shadow-[0_0_0_2px_var(--accent)] disabled:opacity-60"
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={60}>an hour</option>
              <option value={180}>3 hours</option>
              <option value={1440}>a day</option>
              <option value={0}>until I take it down</option>
            </select>
          </label>

          <span className="min-w-4 flex-1" />

          {notice.on ? (
            <Button size="sm" kind="ghost" disabled={!isAdmin || save.isPending} onClick={() => save.mutate({ on: false })}>
              Take it down
            </Button>
          ) : null}
          <Button
            size="sm"
            kind="primary"
            disabled={!isAdmin || !text || save.isPending}
            onClick={() => save.mutate({ on: true, message: text, level, minutes })}
          >
            {save.isPending ? 'Putting it up…' : notice.on ? 'Update it' : 'Show everybody'}
          </Button>
        </div>

        {notice.by && notice.at ? (
          <p className="m-0 text-[11.5px] text-ghost">
            Last set by {notice.by} · {new Date(notice.at).toLocaleString()}
            {notice.until ? ' · ends ' + new Date(notice.until).toLocaleString() : ' · no end date'}
          </p>
        ) : null}
      </div>
    </section>
  );
}

/**
 * The numbers that decide what counts as a problem.
 *
 * They were constants picked for a two-core box, so every other machine got
 * thresholds tuned for somebody else's. On a bigger server 120ms of lag is
 * worth shouting about; on a small one under load it is Tuesday - and a
 * warning that is always on is a warning nobody reads, which is how a real
 * one gets missed.
 *
 * Every field is clamped on the server, not here: the page can be wrong
 * about the range and the guard still cannot be turned into something that
 * throttles somebody for a week.
 */
function Tuning({ tuning, isAdmin }: { tuning: TuningState | undefined; isAdmin: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Record<string, number>>({});

  const save = useMutation({
    mutationFn: (patch: Record<string, number>) => api.post('/api/admin/guard/tuning', patch),
    onSuccess: () => {
      setDraft({});
      toast('Thresholds saved', 'good');
      void qc.invalidateQueries({ queryKey: ['admin', 'console'] });
    },
    onError: (e) => toast((e as Error).message, 'bad')
  });

  const rows = Object.entries(tuning ?? {});
  if (!rows.length) return null;

  const changed = Object.keys(draft).length > 0;
  const moved = rows.filter(([k, s]) => (draft[k] ?? s.value) !== s.def).length;

  return (
    <section className="rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--line-soft)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 rounded-[calc(10px*var(--radius-mul,1))] text-left outline-none focus-visible:shadow-[0_0_0_2px_var(--accent)]"
      >
        <h3 className="m-0 flex-1 text-[15px] font-bold text-ink">What counts as a problem</h3>
        {moved ? (
          <span className="rounded-full bg-accent-soft px-2.5 py-1 text-[12px] font-bold text-accent">
            {moved} moved
          </span>
        ) : null}
        <Icon
          name="chevronD"
          size={15}
          className={cn('flex-none opacity-50 transition-transform duration-[260ms] ease-[var(--ease-out)] motion-reduce:transition-none', open && 'rotate-180')}
        />
      </button>

      <div
        className="grid transition-[grid-template-rows,opacity] duration-[260ms] ease-[var(--ease-out)] motion-reduce:transition-none"
        style={{ gridTemplateRows: open ? '1fr' : '0fr', opacity: open ? 1 : 0 }}
        inert={!open}
      >
        <div className="overflow-hidden">
          <p className="m-0 pt-3 pb-3 text-[12.5px] text-mute">
            The defaults were picked for a small server. Raise them on a bigger one so the warnings above mean something; the server refuses anything that would stop the guard being a guard.
          </p>

          <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
            {rows.map(([key, spec]) => {
              const value = draft[key] ?? spec.value;
              const off = value !== spec.def;
              return (
                <li key={key} className="flex flex-wrap items-center gap-3">
                  <span className="min-w-[150px] flex-1">
                    <span className="block text-[13.5px] font-semibold text-ink">{spec.label}</span>
                    <span className="block text-[11.5px] text-ghost">
                      {spec.min}–{spec.max}{spec.unit ? ' ' + spec.unit : ''} · default {spec.def}
                    </span>
                  </span>
                  <input
                    type="range"
                    min={spec.min}
                    max={spec.max}
                    value={value}
                    disabled={!isAdmin}
                    onChange={(e) => setDraft((d) => ({ ...d, [key]: Number(e.target.value) }))}
                    className="h-1.5 w-[160px] flex-none cursor-pointer appearance-none rounded-full bg-surface-2 accent-[var(--accent)] outline-none disabled:opacity-50"
                  />
                  <span
                    className={cn(
                      'w-[86px] flex-none rounded-[calc(8px*var(--radius-mul,1))] px-2 py-1 text-right text-[12.5px] font-bold tabular-nums',
                      off ? 'bg-accent-soft text-accent' : 'text-dim'
                    )}
                  >
                    {value}{spec.unit}
                  </span>
                </li>
              );
            })}
          </ul>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              kind="ghost"
              disabled={!isAdmin || save.isPending}
              onClick={() => {
                const back: Record<string, number> = {};
                for (const [k, s] of rows) back[k] = s.def;
                save.mutate(back);
              }}
            >
              Put them all back
            </Button>
            <span className="min-w-4 flex-1" />
            {changed ? (
              <Button size="sm" kind="outline" onClick={() => setDraft({})}>Cancel</Button>
            ) : null}
            <Button
              size="sm"
              kind="primary"
              disabled={!isAdmin || !changed || save.isPending}
              onClick={() => save.mutate(draft)}
            >
              {save.isPending ? 'Saving…' : 'Save thresholds'}
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

function Debug({ d }: { d: Console }) {
  const rows: [string, string][] = [
    ['Version', d.version + ' · build ' + d.build],
    ['Node', d.node],
    ['Platform', d.platform],
    ['Host', d.host.platform + ' · ' + d.host.release],
    ['Process uptime', duration(d.uptime)],
    ['Host uptime', duration(d.host.hostUptime)],
    ['Accounts', d.counts.users.toLocaleString()],
    ['Online', String(d.counts.online)],
    ['Open parties', String(d.counts.parties)],
    ['Sockets', String(d.counts.sockets)],
    ['Sessions', String(d.counts.sessions)],
    ['Messages', d.counts.messages.toLocaleString()],
    ['Pugo data on disk', mb(d.disk.dataBytes)],
    ['Guard', d.guard.enabled ? 'armed' : 'reporting only'],
    ['Paused', d.switches.filter((s) => !s.on).map((s) => s.name).join(', ') || 'nothing']
  ];
  return (
    <section className="rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--line-soft)]">
      <h3 className="m-0 mb-3 text-[15px] font-bold text-ink">Everything else</h3>
      <dl className="m-0 grid gap-x-4 gap-y-1 text-[12.5px] [grid-template-columns:auto_1fr]">
        {rows.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-mute">{k}</dt>
            <dd className="m-0 min-w-0 truncate tabular-nums text-dim">{v}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/**
 * The server console as a page of its own.
 *
 * Its own route rather than a tab inside the staff console, because "is the
 * server all right" and "somebody is being a problem" are two different jobs
 * at two different moments, and making one wait behind the other is how a
 * check stops happening.
 *
 * Moderators may read it. Only admins may flip a switch or arm the guard,
 * which the console enforces itself - this only decides what it says at the
 * top.
 */
export function ServerConsolePage() {
  const me = useSession((s) => s.me);
  const isAdmin = me?.role === 'admin';

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-[1040px] px-4 py-6 sm:px-6">
        <header className="mb-5">
          <p className="m-0 text-[12px] font-bold tracking-[.2em] text-mute uppercase">Server</p>
          <h1 className="mt-1 mb-0 text-[30px] leading-[1.05] font-extrabold tracking-[-.03em] text-ink">
            How it is holding up
          </h1>
          <p className="mt-1.5 mb-0 text-[15px] text-mute">
            {isAdmin
              ? 'Live. You can pause parts of Pugo from here, and arm the guard.'
              : 'Live. Pausing anything is an administrator’s to do.'}
          </p>
        </header>
        <ServerConsole isAdmin={isAdmin} />
      </div>
    </div>
  );
}
